import { useState, type RefObject } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { useTenantSettings } from '../../contexts/TenantSettingsContext';
import { wrapWithTenantBrand, a4ContentWidthPx } from '../../lib/pdf-brand';

/**
 * Bouton partagé "Export PDF" (US-TR-08).
 *
 * Cible un nœud DOM via `targetRef` et appelle `html2pdf.js` (import dynamique
 * — n'alourdit pas le bundle initial). Options par défaut : A4 portrait,
 * marges 10 mm. Conçu pour les fiches détaillées (drawers/pages détail) qui
 * doivent pouvoir être exportées avec leur rendu visuel actuel.
 *
 * NB : pour les pages reporting déjà couvertes par `<ExportButtons>`, voir
 * `components/reporting/ExportButtons.tsx`.
 */
export interface PdfExportButtonProps {
  /** Référence au nœud DOM à imprimer en PDF. */
  targetRef: RefObject<HTMLElement | null>;
  /** Nom du fichier final (sans extension, ou avec — peu importe). */
  filename: string;
  /** A4 portrait par défaut. */
  orientation?: 'portrait' | 'landscape';
  /** Marges PDF en millimètres (défaut 10). */
  marginsMm?: number;
  /** Libellé du bouton (défaut "Export PDF"). */
  label?: string;
  /** Classes Tailwind du bouton (défaut style ghost). */
  className?: string;
  /**
   * Hook appelé AVANT lecture de `targetRef` — utile quand la cible n'est
   * montée que sous certaines conditions (ex. un onglet « Informations » de
   * drawer). Permet de basculer sur l'onglet portant la cible : le bouton
   * attend ensuite que `targetRef` apparaisse dans le DOM avant d'exporter.
   */
  onBeforeExport?: () => void | Promise<void>;
}

/** Attend que la ref soit montée (après un changement d'onglet/render). */
async function waitForRef(
  ref: RefObject<HTMLElement | null>,
  timeoutMs = 1500,
): Promise<HTMLElement | null> {
  const start = performance.now();
  while (!ref.current && performance.now() - start < timeoutMs) {
    await new Promise((r) => requestAnimationFrame(() => r(null)));
  }
  return ref.current;
}

export function PdfExportButton({
  targetRef,
  filename,
  orientation = 'portrait',
  marginsMm = 10,
  label = 'Export PDF',
  className,
  onBeforeExport,
}: PdfExportButtonProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { tenant, settings } = useTenantSettings();

  async function handleClick() {
    setLoading(true);
    setErrorMessage(null);
    // Permet à l'appelant de rendre la cible disponible (ex. bascule d'onglet),
    // puis attend qu'elle soit effectivement montée dans le DOM.
    await onBeforeExport?.();
    const targetEl = targetRef.current ?? (await waitForRef(targetRef));
    if (!targetEl) {
      setErrorMessage('Contenu à exporter introuvable');
      setLoading(false);
      return;
    }
    const wrap = wrapWithTenantBrand(
      targetEl,
      {
        name: tenant?.nom,
        logoUrl: tenant?.logo_signed_url ?? tenant?.logo_url,
        primaryColor: settings?.branding?.primary_color,
      },
      { widthPx: a4ContentWidthPx(orientation, marginsMm) },
    );
    try {
      const mod = await import('html2pdf.js');
      const html2pdf = mod.default;
      const safeName = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
      await html2pdf()
        .from(wrap.target)
        .set({
          margin: marginsMm,
          filename: safeName,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation },
        })
        .save();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Erreur export PDF');
    } finally {
      wrap.cleanup();
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      title={errorMessage ?? label}
      className={
        className ??
        'inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60'
      }
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin text-red-600" />
      ) : (
        <FileText className="w-3.5 h-3.5 mr-1 text-red-600" />
      )}
      {label}
    </button>
  );
}
